import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import UserService from "../../services/UserService";
import RevenueRadarLogo from "../svg/RevenueRadarLogo";
import { requireLogin } from "../HelperFunction/helperFunction";

const { REACT_APP_REDIRECT_URI } = process.env;

const BRAND_MANAGER_ROLES = ["BBMNGR", "OODMNGR", "CBMNGR", "MUMNGR", "SALES", "RETAIL"];

const NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: "fas fa-chart-pie", roles: [] },
  // Market Intelligence hidden for now.
  // {
  //   to: "/dashboard/market-intelligence",
  //   label: "Market Intelligence",
  //   icon: "fas fa-compass",
  //   roles: [],
  // },
  {
    to: "/dashboard/refreshmodel",
    label: "Refresh Model",
    icon: "fas fa-sync-alt",
    roles: ["adminrole"],
  },
  {
    to: "/cockpit",
    label: "Brand Cockpit",
    icon: "fas fa-chart-pie",
    roles: BRAND_MANAGER_ROLES,
  },
  {
    to: "/dashboard/modelperformance",
    label: "Model Performance",
    icon: "fas fa-chart-line",
    roles: [],
  },
  {
    to: "/dashboard/marketanalysis",
    label: "Market Analysis",
    icon: "fas fa-store",
    roles: [],
  },
  {
    to: "/dashboard/brandanalysis",
    label: "Brand Analysis",
    icon: "fas fa-industry",
    roles: [],
  },
  {
    to: "/dashboard/simulator",
    label: "Scenario Planner",
    icon: "fas fa-flask",
    roles: [],
  },
  {
    to: "/dashboard/optimizer",
    label: "Optimized Spends",
    icon: "fas fa-bullseye",
    roles: [],
  },
  {
    to: "/dashboard/savedscenarios",
    label: "Saved Scenarios",
    icon: "fas fa-layer-group",
    roles: [],
  },
  {
    to: "/dashboard/savedreports",
    label: "Saved Reports",
    icon: "fas fa-bookmark",
    roles: [],
  },
];

const getInitials = (name = "") => {
  const words = name.split(/[^A-Za-z]+/).filter(Boolean);
  const letters = words.length > 1 ? words[0][0] + words[1][0] : name.slice(0, 2);
  return letters.toUpperCase() || "U";
};

const THEME_OPTIONS = [
  { value: "light", label: "Light", icon: "fas fa-sun" },
  { value: "dark", label: "Dark", icon: "fas fa-moon" },
];

function Navbar3({ theme = "light", toggleTheme, collapsed, setCollapsed }) {
  const location = useLocation();
  const isLoggedIn = UserService.isLoggedIn();
  const username = UserService.getUsername()?.toUpperCase();
  const isBrandManager = UserService.hasRole(BRAND_MANAGER_ROLES);
  const isAdmin = UserService.hasRole(["adminrole"]);

  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 991.98);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 991.98);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (location.pathname === "/") return null;

  const effectiveCollapsed = !isMobile && collapsed;
  const isDashboardHome = location.pathname === "/dashboard";

  const filteredNavItems = NAV_ITEMS.filter((item) => {
    if (!item.roles.length) return true;
    return item.roles.some((role) => UserService.hasRole([role]));
  });

  const isActive = (path) => {
    if (path === "/dashboard") return location.pathname === "/dashboard";
    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  const handleLogout = () => {
    UserService.doLogout({
      redirectUri: `${REACT_APP_REDIRECT_URI}`,
    });
  };

  return (
    <>
      <aside
        className={`rr-sidebar ${isNavOpen ? "open" : ""} ${
          effectiveCollapsed ? "collapsed" : ""
        }`}
      >
        <div className="rr-sidebar-top">
          <Link className="rr-brand" to="/" title="Revenue Radar">
            {!effectiveCollapsed ? (
              <RevenueRadarLogo
                theme="dark"
                width={176}
                height={61}
                showTagline={true}
                animated={true}
              />
            ) : (
              <div className="rr-brand-mini">RR</div>
            )}
          </Link>

          <div className="rr-sidebar-top-actions">
            <button
              className="rr-collapse-btn d-none d-lg-inline-flex"
              type="button"
              onClick={() => setCollapsed((prev) => !prev)}
              title={effectiveCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <i
                className={`fas ${
                  effectiveCollapsed ? "fa-angle-right" : "fa-angle-left"
                }`}
              ></i>
            </button>

            <button
              className="rr-mobile-close"
              type="button"
              onClick={() => setIsNavOpen(false)}
              aria-label="Close sidebar"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        {!isDashboardHome && isLoggedIn ? (
          <div className="rr-sidebar-nav-wrap">
            {!effectiveCollapsed && (
              <div className="rr-sidebar-section-title">Navigation</div>
            )}

            <ul className="rr-sidebar-nav-list">
              {filteredNavItems.map(({ to, label, icon }) => {
                if (isBrandManager && to === "/dashboard") return null;
                if (isAdmin && to === "/cockpit") return null;

                return (
                  <li className="rr-sidebar-nav-item" key={to}>
                    <NavLink
                      to={to}
                      className={`rr-sidebar-link ${
                        isActive(to) ? "active-sidebar-link" : ""
                      }`}
                      onClick={() => setIsNavOpen(false)}
                      title={effectiveCollapsed ? label : ""}
                    >
                      <span className="rr-sidebar-link-icon">
                        <i className={icon}></i>
                      </span>

                      {!effectiveCollapsed && (
                        <span className="rr-sidebar-link-text">{label}</span>
                      )}

                      {effectiveCollapsed && (
                        <span className="rr-collapsed-tooltip">{label}</span>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <div className="rr-sidebar-nav-wrap" />
        )}

        <div className="rr-sidebar-bottom">
          <div
            className="rr-theme-switch"
            role="group"
            aria-label="Theme"
          >
            {THEME_OPTIONS.map((option) => {
              const isCurrent = theme === option.value;
              if (effectiveCollapsed && isCurrent) return null;
              return (
                <button
                  key={option.value}
                  type="button"
                  className={`rr-theme-switch-btn ${isCurrent ? "is-active" : ""}`}
                  aria-pressed={isCurrent}
                  title={`${option.label} mode`}
                  onClick={() => !isCurrent && toggleTheme?.()}
                >
                  <i className={option.icon}></i>
                  {!effectiveCollapsed && <span>{option.label}</span>}
                </button>
              );
            })}
          </div>

          {isLoggedIn ? (
            <div
              className="rr-side-user"
              title={effectiveCollapsed ? username || "User" : ""}
            >
              <div className="rr-side-user-avatar" aria-hidden="true">
                {getInitials(username)}
              </div>

              {!effectiveCollapsed && (
                <div className="rr-side-user-meta">
                  <div className="rr-side-user-name">{username || "User"}</div>
                  <div className="rr-side-user-status">Signed in</div>
                </div>
              )}

              <button
                className="rr-side-user-logout"
                onClick={handleLogout}
                type="button"
                title="Logout"
                aria-label="Logout"
              >
                <i className="fas fa-sign-out-alt"></i>
              </button>
            </div>
          ) : (
            <button
              className="rr-login-btn w-100"
              type="button"
              onClick={() => requireLogin("/dashboard")}
              title="Login"
            >
              <i className="fas fa-sign-in-alt"></i>
              {!effectiveCollapsed && <span>Login</span>}
            </button>
          )}
        </div>
      </aside>

      <button
        className={`rr-sidebar-toggle ${isNavOpen ? "open" : ""}`}
        type="button"
        onClick={() => setIsNavOpen((prev) => !prev)}
        aria-label="Toggle sidebar"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      {isNavOpen && (
        <div
          className="rr-sidebar-backdrop"
          onClick={() => setIsNavOpen(false)}
        ></div>
      )}
    </>
  );
}

export default Navbar3;
